import type { Media } from 'types';
import type { WrapProps } from '@leafwell/components';
import type { PageProps } from 'lib/queries/pages';
import React from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { Paragraph, Wrap } from '@leafwell/components';
import SignupForm from 'components/signup-form';
import HeadingBody from 'components/heading-body';
import { useWordPressContext } from 'contexts/wordpress-provider';

export type HeroCardProps = Partial<Pick<WrapProps, 'className' | 'spacer'>> &
  Pick<
    PageProps['page']['acf'],
    | 'hero'
    | 'states'
    | 'formUrl'
    | 'pageType'
    | 'affiliateLogo'
    | 'affiliateWebsite'
    | 'pauseAppointments'
    | 'redirectMedicalCard'
    | 'redirectCannabisCounseling'
  > & {
    redirectStatePage?: boolean;
    variant?: PageProps['variant'];
    showTrustpilot?: boolean;
    showBadges?: boolean;
    showStateSelect?: boolean;
  };

const AffiliateLogo = dynamic(() => import('./affiliate-logo'), {
  loading: () => <div className="h-28"></div>,
  ssr: false,
});

export type BagdeProps = {
  label?: string;
  icon?: Omit<Media, 'srcSet'>;
};

const HeroBadge: React.FC<BagdeProps> = ({ icon, label }) => (
  <article className="flex flex-col gap-y-2 xl:flex-row gap-x-2 items-center">
    <Image
      src={icon.src}
      width={48}
      height={48}
      alt="values header icon"
      quality={50}
      priority={true}
      className="flex h-auto justify-center items-end"
    />
    <Paragraph className="max-w-24 xl:max-w-28	text-xs xl:text-sm font-medium text-center xl:text-left">
      {label}
    </Paragraph>
  </article>
);

const HeroCardForm: React.FC<
  HeroCardProps & { redirectOnStateChange?: boolean; badges?: BagdeProps[] }
> = ({
  hero,
  badges,
  states,
  formUrl,
  pageType = 'card',
  redirectOnStateChange = true,
  redirectMedicalCard,
  redirectCannabisCounseling,
  affiliateLogo,
  affiliateWebsite,
}) => {
  return (
    <section className="relative grid bg-lilac-50 xl:-mt-40 xl:pt-40">
      <Wrap
        {...{
          className: [
            'grid lg:grid-cols-2 xl:grid-cols-3 gap-y-32 items-center gap-x-10',
          ].join(' '),
          spacer: true,
        }}
      >
        <div className="relative flex flex-col xl:col-span-2 gap-y-6 lg:gap-y-10 text-pretty leafwell-mc-bg">
          {affiliateLogo && (
            <AffiliateLogo
              affiliateLogo={affiliateLogo}
              affiliateWebsite={affiliateWebsite}
            />
          )}
          <HeadingBody
            tagName="h1"
            className="lg:mb-0 font-sans !text-3xl md:!text-4xl lg:!text-5xl xl:!text-7xl text-center lg:text-left text-balance"
            title={hero.title}
          />
          {hero.description?.length > 0 ? (
            <div
              className="text-center lg:text-left"
              dangerouslySetInnerHTML={{ __html: hero.description }}
            ></div>
          ) : null}
          {badges?.length > 0 ? (
            <div
              className={[
                'flex justify-center lg:justify-start gap-x-6',
                !hero.description?.length ? 'lg:mt-8' : '',
              ].join(' ')}
            >
              {badges?.map(({ label, icon }) => {
                return <HeroBadge key={icon.id} label={label} icon={icon} />;
              })}
            </div>
          ) : null}
        </div>
        <div className="w-full max-w-md justify-self-center lg:justify-self-end">
          <SignupForm
            formUrl={formUrl}
            pageType={pageType}
            activeStates={states}
            redirectMedicalCard={redirectMedicalCard}
            redirectOnStateChange={redirectOnStateChange}
            redirectCannabisCounseling={redirectCannabisCounseling}
            formSubmitText={hero.formSubmitText}
          />
          <div className="mt-4 text-center">
            <p className="text-sm">
              Already a Leafwell patient?
              <br />
              <a
                className="font-medium underline underline-offset-3"
                target="_blank"
                href={process.env.NEXT_PUBLIC_LOGIN_URL}
              >
                Log in here
              </a>
            </p>
          </div>
        </div>
      </Wrap>
    </section>
  );
};

const HeroCard: React.FC<HeroCardProps> = props => {
  const { disableMedicalCard, translations } = useWordPressContext();
  const {
    redirectStatePage,
    affiliateLogo,
    pauseAppointments,
    redirectMedicalCard,
  } = props;

  let redirectOnStateChange = true;

  if (redirectStatePage) {
    redirectOnStateChange = redirectStatePage;
  }

  if (disableMedicalCard) {
    redirectOnStateChange = false;
  }

  // this is an affiliate page
  if (affiliateLogo) {
    redirectOnStateChange = false;
  }

  // this page isn't active on medical card and a hubspot form is displayed
  if (pauseAppointments || !redirectMedicalCard) {
    redirectOnStateChange = false;
  }

  const badges = [
    {
      label: translations['Best Care, Best Value'],
      icon: {
        alt: 'Best Care, Best Value',
        title: 'diamond-solid',
        id: '1',
        src: '/diamond-solid.svg',
        srcSet: null,
        width: 18,
        height: 18,
      },
    },
    {
      label: translations['Quick and Easy Process'],
      icon: {
        alt: 'Quick and Easy Process',
        title: 'clock-solid',
        id: '2',
        src: '/clock-solid.svg',
        srcSet: null,
        width: 18,
        height: 18,
      },
    },
    {
      label: translations['Get Approved or Your Money Back'],
      icon: {
        alt: 'Get Approved or Your Money Back',
        title: 'hand-dollar-solid',
        id: '3',
        src: '/hand-dollar-solid.svg',
        srcSet: null,
        width: 18,
        height: 18,
      },
    },
  ];

  return (
    <HeroCardForm
      badges={badges}
      redirectMedicalCard={redirectMedicalCard}
      redirectOnStateChange={redirectOnStateChange}
      {...props}
    />
  );
};

export default HeroCard;
